<template>
  <div class="py-2 my-2 help-container">
    <h1 class="separator py-4">Bulk Calculations / Assumptions</h1>
    <span class="banner py-3 mb-4">
      <div>This is going to allow you to distribute dollar amounts or assumption statements to multiple properties or Property Groups.  You will only be able to select a single GL Account.  You may have to do multiple calculations if you need to impact multiple GL Accounts.</div>
    </span>

    <!--Help Sections-->
    <v-expansion-panels v-model="openSectionInternal">
      <v-expansion-panel>
        <v-expansion-panel-header :color="getSectionColorFor(0)">
          <v-icon small class="icon" slot="actions" :color="getIconColorFor(0)">$vuetify.icons.expand</v-icon>
          <span class="header pl-3"><strong>Creating the Calculations / Assumptions</strong></span>
        </v-expansion-panel-header>
        <v-expansion-panel-content class="help-content">
          <div>You will be able to select four types of formulas:</div>
          <div><strong>Flat Fee</strong></div>
          <ul>
            <li>Will populate a flat dollar amount to a GL Account for multiple properties.</li>
            <li>Example:  I would like to populate the Printing Expense GL Account with $50.00 per month.</li>
            <li>
              <img src="../../assets/help/bc1.png" />
            </li>
          </ul>
          <div><strong>Fee per unit</strong></div>
          <ul>
            <li>Will allow you to create a budget amount that is a dollar amount multiplied by the Unit Count.</li>
            <li>Example:  I would like to populate the Answering Service Expense GL Account with $1.00 per unit.</li>
            <li>
              <img src="../../assets/help/bc2.png" />
            </li>
          </ul>
          <div><strong>Percentage split based on unit count</strong></div>
          <ul>
            <li>Will allow you to take a dollar amount and distribute it amount property groups allocated by their Unit count.</li>
            <li>Example:  I would like to populate the Training Expense GL Account with the properties pro-rata share of $100.00.</li>
            <li>
              <img src="../../assets/help/bc3.png" />
            </li>
          </ul>
          <div><strong>Assumption text only (no calculations)</strong></div>
          <ul>
            <li>This option will allow you to create just an Assumption statement for a GL Account for multiple properties.</li>
            <li>Example:  I would like to populate the Training Expense GL Account with the following Assumption statement:  “The Training expenses are based on the properties share of expenses on a pro-rata basis.”</li>
            <li>
              <img src="../../assets/help/bc4.png" />
            </li>
          </ul>
        </v-expansion-panel-content>
      </v-expansion-panel>
      <v-expansion-panel>
        <v-expansion-panel-header :color="getSectionColorFor(1)">
          <v-icon small class="icon" slot="actions" :color="getIconColorFor(1)">$vuetify.icons.expand</v-icon>
          <span class="header pl-3"><strong>Field Details</strong></span>
        </v-expansion-panel-header>
        <v-expansion-panel-content class="help-content">          
          <ul>
            <li><strong>15-Character max description:</strong> Will be used as the description for the new Working Row that will be created.</li>
            <li><strong>Optional Assumption Statement:</strong> This is a 100 character field and is optional unless you are using the Assumption Text Only formula.  If an existing Assumption statement already exists for that GL Account, this text will be added afterwards. If there is not enough room, this description will be truncated (cut off).</li>
          </ul>
        </v-expansion-panel-content>
      </v-expansion-panel>
      <v-expansion-panel>
        <v-expansion-panel-header :color="getSectionColorFor(2)">
          <v-icon small class="icon" slot="actions" :color="getIconColorFor(2)">$vuetify.icons.expand</v-icon>
          <span class="header pl-3"><strong>Confirming a Bulk Calculation</strong></span>
        </v-expansion-panel-header>
        <v-expansion-panel-content class="help-content">         
          <ul>
            <li>Once you have clicked <strong>Continue</strong>, the system will look for any errors. If none are found, a confirmation popup will appear displaying the results that will occur.</li>
            <li>If they are correct, click <strong>APPLY</strong>.  If there is any errors, you can <strong>CANCEL</strong> this Bulk Calculation.</li>
            <li>
              <img src="../../assets/help/bc5.png" />
            </li>
          </ul>
        </v-expansion-panel-content>
      </v-expansion-panel>
      <v-expansion-panel>
        <v-expansion-panel-header :color="getSectionColorFor(3)">
          <v-icon small class="icon" slot="actions" :color="getIconColorFor(3)">$vuetify.icons.expand</v-icon>
          <span class="header pl-3"><strong>Errors</strong></span>
        </v-expansion-panel-header>
        <v-expansion-panel-content class="help-content">
          <div>If you run into any errors, the entire distribution will fail.</div>
          <div>You must resolve the issue and redo the Bulk Calculation or redo the Bulk Calculation without the problem property.</div>
          <div>
                  <img src="../../assets/help/bc6.png" />
          </div>
        </v-expansion-panel-content>
      </v-expansion-panel>
      <v-expansion-panel>
        <v-expansion-panel-header :color="getSectionColorFor(4)">
          <v-icon small class="icon" slot="actions" :color="getIconColorFor(4)">$vuetify.icons.expand</v-icon>
          <span class="header pl-3"><strong>Result of creating a Bulk Calculation</strong></span>
        </v-expansion-panel-header>
        <v-expansion-panel-content class="help-content">
          <div>Once you have successfully completed your Bulk Calculation that is not Assumption Text only , that GL Account will have two new Working Rows.</div>
         <ul>
           <li>Any existing budget numbers for that GL Account will be on one Working Row.</li>
           <li>The new Bulk Calculation amount(s) will be on a second Working Row.  The 15-character description entered will be used as the Working Row description.</li>
         </ul>
        </v-expansion-panel-content>
      </v-expansion-panel>
      <v-expansion-panel>
        <v-expansion-panel-header :color="getSectionColorFor(5)">
          <v-icon small class="icon" slot="actions" :color="getIconColorFor(5)">$vuetify.icons.expand</v-icon>
          <span class="header pl-3"><strong>Previously Applied section</strong></span>
        </v-expansion-panel-header>
        <v-expansion-panel-content class="help-content">
          <div>This section will display any Bulk Calculations and Assumptions for the year selected. Only years where a Bulk Calculation has been performed will be available in the drop-down.</div>
          <div><img src="../../assets/help/bc7.png" /></div>
          <div>At the bottom is a link to display the confirmation popup, which will show a list of properties and what they were each charged.</div>
          <div><img src="../../assets/help/bc8.png" /></div>
        </v-expansion-panel-content>
      </v-expansion-panel>
      <v-expansion-panel readonly>
        <v-expansion-panel-header>
          <v-icon slot="actions"></v-icon>
          <span class="black--text pointer">Prefer to watch a video? <a @click="openExternalTopic('BudgetsPro Bulk Calculations')">Click Here</a></span>
        </v-expansion-panel-header>
      </v-expansion-panel>
      <v-expansion-panel readonly>
        <v-expansion-panel-header>
          <v-icon slot="actions"></v-icon>
          <span class="black--text pointer">Want to see all BudgetsPro help articles? <a @click="openExternalTopic('BudgetsPro')">Click Here</a></span>
        </v-expansion-panel-header>
      </v-expansion-panel>
    </v-expansion-panels>
  </div>
</template>

<script>
  import HelpMixin from '@/mixins/Help.js'
  export default {
    name: 'BulkCalculationsHelp',
    mixins: [HelpMixin]
  }
</script>

<style lang="scss" scoped>
  .no-bullet {
    list-style-type: none;
  }
  img {
      max-width: 650px;
  }
  img.small {
      width: 35px;
  }
  .icon {
    order: 0;
  }

  .header {
    order: 1;
  }

  .help-container {
    border-bottom: 1px solid var(--v-resGrey-lighten1)
  }

  .help-content div,
  .help-content li {
    margin-top: 10px !important;
    margin-bottom: 10px !important;
  }

  .banner {
    text-align: center;
    background-color: var(--v-resGrey-lighten1);
    display: block;
  }

  .pointer {
    cursor: pointer !important;
  }

  .separator {
    display: flex;
    align-items: center;
    text-align: center;
  }

  .separator::before,
  .separator::after {
    content: '';
    flex: 1;
    border-bottom: 1px solid var(--v-resGrey-darken1);
  }

  .separator:not(:empty)::before {
    margin-right: .25em;
  }

  .separator:not(:empty)::after {
    margin-left: .25em;
  }
</style>
